import React, { FunctionComponent } from 'react'
import { useTranslation } from '../../../utils/convergence'
import { usePclPaymentOptions, usePbiPaymentOption, useIsAfterpayFinanceFeeEligible } from '../../../state'
import { PaymentOption } from '../../../model'
import { PaymentOptionLabel } from '../../PaymentOptionLabel'
import { Heading, Radio, useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import { useCheckoutSizes } from '../../../utils/responsive'
import { ConsumerLendingTagline } from '../../ConsumerLendingTagline'
import styles from './style.module.css'

interface PaymentOptionsRadioGroupProps {
  lock: boolean
  selectedPaymentOptionLocal?: PaymentOption
  setSelectedPaymentOptionLocal: (paymentOptions: PaymentOption) => void
}

export const PaymentOptionsRadioGroup: FunctionComponent<PaymentOptionsRadioGroupProps> = ({
  selectedPaymentOptionLocal,
  setSelectedPaymentOptionLocal,
  lock,
}) => {
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const { t } = useTranslation()
  const { textSize } = useCheckoutSizes()
  const pclPaymentOptions = usePclPaymentOptions()
  const pbiPaymentOption = usePbiPaymentOption()
  const isAFF = useIsAfterpayFinanceFeeEligible()

  const renderPaymentOption = (paymentOption: PaymentOption): JSX.Element => {
    const selected = paymentOption.id === selectedPaymentOptionLocal?.id

    return (
      <Radio.Button
        testNameSpace={`payment-option-${paymentOption.id}`}
        key={`payment-option-${paymentOption.id}`}
        value={paymentOption.id.toString()}
        reversed
        roundedBorder
        selectedBorder={selected && !isArcadeDesignLanguage}
        label={() => <PaymentOptionLabel paymentOption={paymentOption} selected={selected} />}
      />
    )
  }

  return (
    <Radio.Group
      id='payment-options'
      name='payment-options'
      testNameSpace='payment-options'
      kind='Option'
      labelWidth='Full'
      labelPlacement='Top'
      value={selectedPaymentOptionLocal?.id.toString()}
      disabled={lock}
      onClick={(e) => {
        const clickedPaymentOptionId = Number((e.target as HTMLButtonElement).value)
        setSelectedPaymentOptionLocal(
          pclPaymentOptions.concat(pbiPaymentOption).find((paymentOption) => paymentOption.id === clickedPaymentOptionId) as PaymentOption
        )
      }}
    >
      <div className={styles.pclTagline}>
        <ConsumerLendingTagline />
      </div>
      {pclPaymentOptions.map(renderPaymentOption)}
      {pbiPaymentOption !== undefined && (
        <div className={styles.pbiHeading}>
          <Heading size={textSize}>
            {t('consumerLending:payInFourTagline', { context: isAFF ? 'aff' : '', interval: pbiPaymentOption.installmentsCount })}
          </Heading>
        </div>
      )}
      {pbiPaymentOption !== undefined && renderPaymentOption(pbiPaymentOption)}
    </Radio.Group>
  )
}
